import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  StatusType,
  tableRowSize,
  stickyHeader,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import {
  earningOvertimeHeader,
  earningTypeColors,
} from "pages/PayRoll/PayRollHeaders";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";

export const EarningOvertime = () => {
  const { domain, userScreenAccess, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [overtimeDetails, setOvertimeDetails] = useState([]);
  const containerRef = useRef(null);
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Earnings",
    "Overtime"
  );

  const {
    result: overtimeData,
    loading,
    setLoad: reCallApi,
  } = useFetch(
    `${domain}payroll/earning/overtime/earning_details/?company_id=${topbarCompanyId}`
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOvertimeDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [reCallApi, topbarCompanyId]);

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Overtime Earning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor additional earnings for extra hours worked beyond scheduled
            shifts.
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Overtime Earning Records"
            filterHeaders={earningOvertimeHeader}
            data={overtimeData}
            filteredData={overtimeDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {earningOvertimeHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {overtimeDetails?.length === 0 ? (
                    <InfoMessage dataLength={overtimeData} />
                  ) : (
                    overtimeDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {earningOvertimeHeader.map((head) => (
                            <Mui.TableCell>
                              {head.key === "ot_consideration_type" ? (
                                <span
                                  style={StatusType(
                                    value.ot_consideration_type,
                                    earningTypeColors
                                  )}
                                >
                                  {value.ot_consideration_type}
                                </span>
                              ) : head.key === "basis" ? (
                                value?.basis ?? "N/A"
                              ) : (
                                value[head.key] ?? "0"
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            {overtimeDetails?.length > 25 && (
              <Box sx={{ mt: 1 }}>
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={overtimeDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            )}
          </Mui.Grid>
        </Paper>
      </Box>
    </React.Fragment>
  );
};
